'use client'

import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  FileUp,
  Heart,
  Image as ImageIcon,
  Loader2,
  MessageSquare,
  Pin,
  Plus,
  Search,
  ShieldCheck,
  Star,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ContentPage } from '@/components/content-page'
import { UserAvatar } from '@/components/user-avatar'
import {
  forumTopicMatchesSearch,
  matchingForumReply,
  matchingForumReplyExcerpt,
} from '@/lib/forum-search'
import { categoryLabel, forumCategories, forumTags } from '@/lib/forum-options'

type User = {
  id: number
  email: string
  handle: string | null
  avatarPath: string | null
  disclaimerAccepted: boolean
}

type Topic = {
  id: number
  title: string
  body: string
  category: string
  tags: string[]
  created_at: string
  last_reply_at: string
  reply_count: number
  replies: TopicReply[]
  is_solved: boolean
  is_pinned: boolean
  is_featured: boolean
  like_count: number
  liked_by: string[]
  author_handle: string
  author_avatar: string | null
  author_callsign: string | null
  author_radios: string | null
  author_badges: string[]
}

type TopicReply = {
  id: number
  author_handle: string
  body: string
  created_at: string
}

function formatDate(s: string) {
  const date = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatReplyDateTime(s: string) {
  const date = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function ReplyBadge({ topic }: { topic: Topic }) {
  const replies = topic.replies ?? []
  const authors = [...new Set(replies.map((reply) => reply.author_handle))]
  const replyLabel =
    replies.length > 0
      ? `${topic.reply_count} ${topic.reply_count === 1 ? 'reply' : 'replies'} from ${authors.join(', ')}`
      : 'No replies yet'

  return (
    <span className="group/replies relative shrink-0">
      <span
        aria-hidden="true"
        className="flex cursor-help items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 font-mono text-xs text-muted-foreground transition-colors group-hover/replies:border-primary/40 group-hover/replies:text-foreground"
      >
        <MessageSquare className="h-3.5 w-3.5 text-primary" />
        {topic.reply_count}
      </span>
      <span className="sr-only">{replyLabel}</span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 bottom-full z-30 w-80 max-w-[calc(100vw-2rem)] origin-bottom-right translate-y-1 opacity-0 transition-[opacity,transform] duration-150 group-hover/replies:pointer-events-auto group-hover/replies:translate-y-0 group-hover/replies:opacity-100 group-focus-visible:pointer-events-auto group-focus-visible:translate-y-0 group-focus-visible:opacity-100"
      >
        <span
          onClick={(event) => event.preventDefault()}
          className="block max-h-[min(24rem,60vh)] overflow-y-auto overscroll-contain rounded-lg border border-border bg-popover px-3 py-2 text-left text-popover-foreground shadow-lg"
        >
          {replies.length > 0 ? (
            <>
              <span className="block text-[10px] font-semibold tracking-wide text-foreground uppercase">
                {replies.length === 1 ? '1 reply' : `${replies.length} replies`}
              </span>
              <span className="mt-2 block border-t border-border">
                {replies.map((reply) => (
                  <span
                    key={reply.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 border-b border-border py-1.5 last:border-b-0"
                  >
                    <span className="truncate font-medium text-foreground">
                      {reply.author_handle}
                    </span>
                    <time className="text-right text-[11px] text-muted-foreground tabular-nums">
                      {formatReplyDateTime(reply.created_at)}
                    </time>
                  </span>
                ))}
              </span>
            </>
          ) : (
            <span className="block text-xs text-muted-foreground">No replies yet</span>
          )}
        </span>
      </span>
    </span>
  )
}

function LikeBadge({ topic }: { topic: Topic }) {
  const likedBy = topic.liked_by ?? []
  const hiddenLikes = Math.max(0, topic.like_count - likedBy.length)
  return (
    <span className="group/likes relative shrink-0">
      <span
        aria-hidden="true"
        className="flex cursor-help items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 font-mono text-xs text-muted-foreground transition-colors group-hover/likes:border-red-300 group-hover/likes:text-foreground"
      >
        <Heart
          className="h-3.5 w-3.5 text-red-500"
          fill={topic.like_count > 0 ? 'currentColor' : 'none'}
        />
        {topic.like_count}
      </span>
      <span className="sr-only">
        {topic.like_count === 1 ? '1 like' : `${topic.like_count} likes`} on the original post
      </span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 bottom-full z-30 w-64 max-w-[calc(100vw-2rem)] origin-bottom-right translate-y-1 opacity-0 transition-[opacity,transform] duration-150 group-hover/likes:pointer-events-auto group-hover/likes:translate-y-0 group-hover/likes:opacity-100"
      >
        <span
          onClick={(event) => event.preventDefault()}
          className="block max-h-56 overflow-y-auto overscroll-contain rounded-lg border border-border bg-popover px-3 py-2 text-left text-popover-foreground shadow-lg"
        >
          <span className="block text-[10px] font-semibold tracking-wide text-foreground uppercase">
            {topic.like_count === 1 ? '1 like' : `${topic.like_count} likes`}
          </span>
          {likedBy.length > 0 ? (
            <span className="mt-2 block border-t border-border">
              {likedBy.map((handle) => (
                <span
                  key={handle}
                  className="block truncate border-b border-border py-1.5 font-medium text-foreground last:border-b-0"
                >
                  {handle}
                </span>
              ))}
              {hiddenLikes > 0 && (
                <span className="block pt-1.5 text-[11px] font-normal text-muted-foreground">
                  {hiddenLikes === 1
                    ? '1 more like kept private'
                    : `${hiddenLikes} more likes kept private`}
                </span>
              )}
            </span>
          ) : (
            <span className="mt-2 block text-xs text-muted-foreground">
              {topic.like_count > 0 ? 'Like attribution is private.' : 'No likes yet.'}
            </span>
          )}
        </span>
      </span>
    </span>
  )
}

function TopicCard({ topic, query }: { topic: Topic; query: string }) {
  const replyMatch = matchingForumReply(topic, query)
  return (
    <a
      href={`/forum/topic/${topic.id}`}
      className="group flex flex-col gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-secondary/50 sm:flex-row sm:items-center"
    >
      <UserAvatar
        name={topic.author_handle}
        src={topic.author_avatar}
        size="md"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {topic.is_pinned && (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              <Pin className="h-3 w-3" />
              Pinned
            </span>
          )}
          {topic.is_solved && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-3 w-3" />
              Solved
            </span>
          )}
          <span className="text-[11px] text-primary">{categoryLabel(topic.category)}</span>
        </div>
        <h3 className="mt-1 font-semibold group-hover:text-primary">{topic.title}</h3>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{topic.author_handle}</span>
          {topic.author_callsign && (
            <span className="font-mono text-xs">{topic.author_callsign}</span>
          )}
          <span>{formatDate(topic.created_at)}</span>
          {topic.reply_count === 0 && (
            <span className="text-[11px] text-primary">Unanswered</span>
          )}
        </div>
        {topic.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            {topic.tags.map((tag) => (
              <span key={tag}>#{tag}</span>
            ))}
          </div>
        )}
        {replyMatch && (
          <p className="mt-2 line-clamp-2 rounded-md bg-secondary/60 px-2.5 py-1.5 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">
              Reply from {replyMatch.author_handle}:
            </span>{' '}
            {matchingForumReplyExcerpt(replyMatch.body, query)}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <LikeBadge topic={topic} />
        <ReplyBadge topic={topic} />
      </div>
    </a>
  )
}

const rules = [
  'Be respectful. No harassment, hate speech, or personal attacks.',
  'No adult, pornographic, or sexually explicit content — including links.',
  'No profanity or slurs. Posts are automatically filtered.',
  'No spam, advertising, or self-promotion.',
  'Keep discussions on topic and helpful.',
  'Moderators may remove content or suspend accounts that break these rules.',
]

export default function ForumPage() {
  const [user, setUser] = useState<User | null>(null)
  const [topics, setTopics] = useState<Topic[]>([])
  const [loading, setLoading] = useState(true)
  const [handle, setHandle] = useState('')
  const [query, setQuery] = useState('')
  const initialSearch = new URLSearchParams(window.location.search)
  const [activeCategory, setActiveCategory] = useState(initialSearch.get('category') || '')
  const [activeTag, setActiveTag] = useState(initialSearch.get('tag') || '')
  const [unansweredOnly, setUnansweredOnly] = useState(
    initialSearch.get('unanswered') === 'true',
  )
  const [agreeing, setAgreeing] = useState(false)
  const [error, setError] = useState('')

  const filteredTopics = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return topics.filter((topic) => {
      if (activeCategory && topic.category !== activeCategory) return false
      if (activeTag && !topic.tags.includes(activeTag)) return false
      if (unansweredOnly && topic.reply_count > 0) return false
      return forumTopicMatchesSearch(topic, needle)
    })
  }, [topics, query, activeCategory, activeTag, unansweredOnly])

  const featuredTopic = filteredTopics.find((topic) => topic.is_featured)
  const featuredReplyMatch = featuredTopic ? matchingForumReply(featuredTopic, query) : null
  const listedTopics = filteredTopics.filter((topic) => topic.id !== featuredTopic?.id)
  const pinnedTopics = listedTopics.filter((topic) => topic.is_pinned)
  const recentTopics = listedTopics.filter((topic) => !topic.is_pinned)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const meRes = await fetch('/api/me')
        const me = (await meRes.json()) as { user: User | null }
        if (cancelled) return
        const u = me.user
        setUser(u)
        setHandle(u?.handle ?? '')
        if (!u || !u.disclaimerAccepted) {
          setLoading(false)
          return
        }
        const topicsRes = await fetch('/api/forum/topics')
        if (topicsRes.ok) {
          const data = (await topicsRes.json()) as { topics: Topic[] }
          setTopics(data.topics)
        }
      } catch {
        // Leave the page in a logged-out state.
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  async function acceptDisclaimer(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setAgreeing(true)
    try {
      const res = await fetch('/api/forum/disclaimer-accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle }),
      })
      const data = (await res.json().catch(() => ({}))) as { error?: string }
      if (!res.ok) throw new Error(data.error || 'Could not save your settings.')
      window.location.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setAgreeing(false)
    }
  }

  const inputClass =
    'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30'

  return (
    <ContentPage
      wide
      eyebrow="// forums"
      title="Community forums"
      description="Talk radios, programming, and everything NewChirp with the community."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !user ? (
        <div className="mx-auto max-w-md rounded-2xl border border-border bg-card p-8 text-center">
          <MessageSquare className="mx-auto h-8 w-8 text-primary" />
          <h2 className="mt-4 text-lg font-semibold">Log in to enter the forums</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The forums are open to registered NewChirp members. Create a free account or log in to
            read topics and join the conversation.
          </p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <Button nativeButton={false} render={<a href="/login" />}>
              Log in
            </Button>
            <Button variant="outline" nativeButton={false} render={<a href="/register" />}>
              Create an account
            </Button>
          </div>
        </div>
      ) : !user.disclaimerAccepted ? (
        <form onSubmit={acceptDisclaimer} className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-semibold">Forum rules &amp; disclaimer</h2>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Before you can take part in the forums, please review the community rules and agree
                to them below.
              </p>
            </div>
          </div>

          <ul className="mt-5 flex flex-col gap-2">
            {rules.map((rule) => (
              <li key={rule} className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                {rule}
              </li>
            ))}
          </ul>

          <label htmlFor="forum-handle" className="mt-5 flex flex-col gap-1.5">
            <span className="text-sm font-medium">
              Your forum handle
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                (3-24 characters, letters/numbers/spaces/dashes/underscores)
              </span>
            </span>
            <input
              id="forum-handle"
              type="text"
              required
              minLength={3}
              maxLength={24}
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="your-handle"
              className={inputClass}
            />
          </label>

          {error && (
            <div
              role="alert"
              className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button type="submit" className="mt-6 w-full" disabled={agreeing}>
            {agreeing && <Loader2 className="h-4 w-4 animate-spin" />}
            I agree &amp; enter the forums
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <section className="overflow-hidden rounded-2xl border border-primary/35 bg-gradient-to-r from-primary/12 via-card to-accent/30 p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-background text-primary">
                  <FileUp className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold">Upload and share files</h2>
                  <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    Send installers, ZIPs, firmware, logs, or radio files. Registered uploads go
                    into quarantine for review before anyone can download them.
                  </p>
                </div>
              </div>
              <Button className="shrink-0" nativeButton={false} render={<a href="/files" />}>
                <FileUp className="h-4 w-4" />
                Open file drop
              </Button>
            </div>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <p className="text-sm text-muted-foreground">
                {topics.length === 0
                  ? 'No topics yet — start the conversation.'
                  : query.trim() || activeCategory || activeTag || unansweredOnly
                    ? `${filteredTopics.length} of ${topics.length} ${topics.length === 1 ? 'topic' : 'topics'}`
                    : `${topics.length} ${topics.length === 1 ? 'topic' : 'topics'}`}
              </p>
              {topics.length > 0 && (
                <span className="rounded-full border border-border bg-card px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                  Most recent first · pinned on top
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" nativeButton={false} render={<a href="/forum/gallery" />}>
                <ImageIcon className="h-4 w-4" />
                Show &amp; tell gallery
              </Button>
              <Button nativeButton={false} render={<a href="/forum/new" />}>
                <Plus className="h-4 w-4" />
                New topic
              </Button>
            </div>
          </div>

          {topics.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search titles, posts, authors, categories, or tags"
                  aria-label="Search forum topics"
                  className="w-full rounded-lg border border-input bg-card py-2.5 pl-9 pr-10 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Clear forum search"
                    className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex gap-2 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() => setActiveCategory('')}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-xs ${
                    !activeCategory
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-muted-foreground'
                  }`}
                >
                  All categories
                </button>
                {forumCategories
                  .filter((category) => category.value !== 'announcement')
                  .map((category) => (
                    <button
                      key={category.value}
                      type="button"
                      onClick={() =>
                        setActiveCategory((current) =>
                          current === category.value ? '' : category.value,
                        )
                      }
                      className={`shrink-0 rounded-full border px-3 py-1.5 text-xs ${
                        activeCategory === category.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-muted-foreground'
                      }`}
                    >
                      {category.label}
                    </button>
                  ))}
                <button
                  type="button"
                  onClick={() => setUnansweredOnly((current) => !current)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-xs ${
                    unansweredOnly
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-muted-foreground'
                  }`}
                >
                  Unanswered
                </button>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {forumTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setActiveTag((current) => (current === tag ? '' : tag))}
                    className={`rounded-full border px-2.5 py-1 text-[11px] ${
                      activeTag === tag
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card text-muted-foreground'
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {topics.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center">
              <p className="text-sm text-muted-foreground">
                Be the first to ask a question or share something useful.
              </p>
              <Button className="mt-4" nativeButton={false} render={<a href="/forum/new" />}>
                <Plus className="h-4 w-4" />
                Create the first topic
              </Button>
            </div>
          ) : filteredTopics.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
              <Search className="mx-auto h-6 w-6 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                No topics match the current search and filters.
              </p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => {
                  setQuery('')
                  setActiveCategory('')
                  setActiveTag('')
                  setUnansweredOnly(false)
                }}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {featuredTopic && (
                <a
                  href={`/forum/topic/${featuredTopic.id}`}
                  className="group relative overflow-hidden rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/20 via-card to-primary/10 p-5 transition-colors hover:border-primary/50"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs font-medium text-primary">
                      <Star className="h-4 w-4" />
                      Featured community post
                    </div>
                    <LikeBadge topic={featuredTopic} />
                  </div>
                  <div className="mt-4 flex items-center gap-4">
                    <UserAvatar
                      name={featuredTopic.author_handle}
                      src={featuredTopic.author_avatar}
                      size="md"
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold group-hover:text-primary">
                        {featuredTopic.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                        {featuredTopic.body}
                      </p>
                      {featuredReplyMatch && (
                        <p className="mt-2 line-clamp-2 rounded-md bg-background/65 px-2.5 py-1.5 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">
                            Reply from {featuredReplyMatch.author_handle}:
                          </span>{' '}
                          {matchingForumReplyExcerpt(featuredReplyMatch.body, query)}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>{categoryLabel(featuredTopic.category)}</span>
                        <span>by {featuredTopic.author_handle}</span>
                        {featuredTopic.tags.map((tag) => (
                          <span key={tag}>#{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </a>
              )}

              {pinnedTopics.length > 0 && (
                <section
                  aria-labelledby="pinned-topics-heading"
                  className="rounded-2xl border border-primary/30 bg-primary/[0.045] p-3 sm:p-4"
                >
                  <div className="mb-3 flex items-center justify-between gap-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-primary/25 bg-background text-primary">
                        <Pin className="h-3.5 w-3.5" />
                      </span>
                      <div>
                        <h2 id="pinned-topics-heading" className="text-sm font-semibold">
                          Pinned topics
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Important information and announcements
                        </p>
                      </div>
                    </div>
                    <span className="rounded-full border border-primary/20 bg-background px-2.5 py-1 font-mono text-[11px] text-muted-foreground">
                      {pinnedTopics.length}
                    </span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {pinnedTopics.map((topic) => (
                      <TopicCard key={topic.id} topic={topic} query={query} />
                    ))}
                  </div>
                </section>
              )}

              {recentTopics.length > 0 && (
                <section
                  aria-labelledby="recent-topics-heading"
                  className={
                    pinnedTopics.length > 0
                      ? 'border-t border-border pt-6'
                      : undefined
                  }
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h2 id="recent-topics-heading" className="text-sm font-semibold">
                        Recent discussions
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        Community topics and questions
                      </p>
                    </div>
                    <span className="rounded-full border border-border bg-card px-2.5 py-1 font-mono text-[11px] text-muted-foreground">
                      {recentTopics.length}
                    </span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {recentTopics.map((topic) => (
                      <TopicCard key={topic.id} topic={topic} query={query} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground">
            Manage your handle and avatar on your{' '}
            <a href="/account" className="text-primary underline underline-offset-4">
              account page
            </a>
            .
          </p>
        </div>
      )}
    </ContentPage>
  )
}
